/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { FinancingHold } from '../models/financing-hold';
import { PlaceFinancingHoldRequest } from '../models/place-financing-hold-request';
import { ReleaseFinancingHoldRequest } from '../models/release-financing-hold-request';
import { ReleaseFinancingHoldsRequest } from '../models/release-financing-holds-request';


/**
 * Financing holds on a client, an end customer or a single invoice.
 * While a hold is active, no financing or final payment is made for the
 * invoices it applies to, and they cannot be accepted out of
 * `WaitingForFinancing` or `WaitingFinalPayment`.
 */
@Injectable({
  providedIn: 'root',
})
export class FinancingHoldsService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation listFinancingHolds
   */
  static readonly ListFinancingHoldsPath = '/financing-holds';

  /**
   * List the financing holds placed on a client, an end customer or an invoice.
   *
   * Returns every hold placed directly on the target, active and
   * released, newest placement first. Released holds carry their
   * release note, releasing user and release time.
   *
   * Name exactly one of `client_id`, `end_customer_id` and
   * `invoice_id`. A target with no holds, or one that does not exist,
   * returns an empty array.
   *
   * This lists the holds placed **on** the target only. The holds that
   * apply to an invoice through its client or its end customer are the
   * invoice's `financing_holds`.
   *
   * `error_code` values returned with `422`:
   *
   * - `financing_hold_invalid_target` — the request names no target
   *   filter, or more than one, or the one filter's value is not a
   *   positive integer. The message of the last names the parameter.
   *
   * Requires authentication only.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listFinancingHolds()` instead.
   *
   * This method doesn't expect any request body.
   */
  listFinancingHolds$Response(params?: {

    /**
     * List the holds placed on this client.
     */
    client_id?: number;

    /**
     * List the holds placed on this end-customer row. Holds on another row with the same business id are not listed, although they apply to this end customer&#x27;s invoices.
     */
    end_customer_id?: number;

    /**
     * List the holds placed on this invoice itself.
     */
    invoice_id?: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Array<FinancingHold>>> {

    const rb = new RequestBuilder(this.rootUrl, FinancingHoldsService.ListFinancingHoldsPath, 'get');
    if (params) {
      rb.query('client_id', params.client_id, {});
      rb.query('end_customer_id', params.end_customer_id, {});
      rb.query('invoice_id', params.invoice_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<FinancingHold>>;
      })
    );
  }

  /**
   * List the financing holds placed on a client, an end customer or an invoice.
   *
   * Returns every hold placed directly on the target, active and
   * released, newest placement first. Released holds carry their
   * release note, releasing user and release time.
   *
   * Name exactly one of `client_id`, `end_customer_id` and
   * `invoice_id`. A target with no holds, or one that does not exist,
   * returns an empty array.
   *
   * This lists the holds placed **on** the target only. The holds that
   * apply to an invoice through its client or its end customer are the
   * invoice's `financing_holds`.
   *
   * `error_code` values returned with `422`:
   *
   * - `financing_hold_invalid_target` — the request names no target
   *   filter, or more than one, or the one filter's value is not a
   *   positive integer. The message of the last names the parameter.
   *
   * Requires authentication only.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `listFinancingHolds$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listFinancingHolds(params?: {

    /**
     * List the holds placed on this client.
     */
    client_id?: number;

    /**
     * List the holds placed on this end-customer row. Holds on another row with the same business id are not listed, although they apply to this end customer&#x27;s invoices.
     */
    end_customer_id?: number;

    /**
     * List the holds placed on this invoice itself.
     */
    invoice_id?: number;
    context?: HttpContext
  }
): Observable<Array<FinancingHold>> {

    return this.listFinancingHolds$Response(params).pipe(
      map((r: StrictHttpResponse<Array<FinancingHold>>) => r.body as Array<FinancingHold>)
    );
  }

  /**
   * Path part for operation placeFinancingHold
   */
  static readonly PlaceFinancingHoldPath = '/financing-holds';

  /**
   * Place one financing hold per named client, end customer and invoice, all or none.
   *
   * Places one active financing hold per named target, all or none.
   * The request names at least one of a client, an end customer and an
   * invoice, in any combination. Each hold records its own target, the
   * trimmed reason, the caller as the placing user, and the placing
   * time, which the holds of one request share. From then on no
   * financing or final payment is made for the invoices a hold applies
   * to, and they cannot be accepted out of `WaitingForFinancing` or
   * `WaitingFinalPayment`, until the hold is released.
   *
   * The response lists the placed holds in the order client, end
   * customer, invoice, also when the request names one target. The
   * holds of one request are independent from then on: each is listed,
   * shown and released on its own.
   *
   * A hold on a client applies to every invoice of the client. A hold on
   * an end customer applies to every invoice billed to that end-customer
   * row, and to every invoice billed to another end-customer row with
   * the same trimmed, non-empty business id. A hold on an invoice
   * applies to that invoice alone.
   *
   * A target may carry several active holds: placing one never changes
   * or releases another. Placing a hold publishes the target's update
   * event on the notification stream (`client_updated`,
   * `end_customer_updated` or `invoice_updated`). A hold on an end
   * customer also publishes `end_customer_updated` for each other
   * end-customer row with the same trimmed, non-empty business id.
   *
   * `error_code` values returned with `422` (nothing is recorded, for
   * any of the named targets):
   *
   * - `financing_hold_invalid_target` — the request names no target, or
   *   a named target does not exist.
   * - `financing_hold_reason_required` — `reason` is empty after
   *   trimming.
   *
   * Requires the `financing-holds:place` permission (403 otherwise).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `placeFinancingHold()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  placeFinancingHold$Response(params: {
    context?: HttpContext
    body: PlaceFinancingHoldRequest
  }
): Observable<StrictHttpResponse<Array<FinancingHold>>> {

    const rb = new RequestBuilder(this.rootUrl, FinancingHoldsService.PlaceFinancingHoldPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<FinancingHold>>;
      })
    );
  }

  /**
   * Place one financing hold per named client, end customer and invoice, all or none.
   *
   * Places one active financing hold per named target, all or none.
   * The request names at least one of a client, an end customer and an
   * invoice, in any combination. Each hold records its own target, the
   * trimmed reason, the caller as the placing user, and the placing
   * time, which the holds of one request share. From then on no
   * financing or final payment is made for the invoices a hold applies
   * to, and they cannot be accepted out of `WaitingForFinancing` or
   * `WaitingFinalPayment`, until the hold is released.
   *
   * The response lists the placed holds in the order client, end
   * customer, invoice, also when the request names one target. The
   * holds of one request are independent from then on: each is listed,
   * shown and released on its own.
   *
   * A hold on a client applies to every invoice of the client. A hold on
   * an end customer applies to every invoice billed to that end-customer
   * row, and to every invoice billed to another end-customer row with
   * the same trimmed, non-empty business id. A hold on an invoice
   * applies to that invoice alone.
   *
   * A target may carry several active holds: placing one never changes
   * or releases another. Placing a hold publishes the target's update
   * event on the notification stream (`client_updated`,
   * `end_customer_updated` or `invoice_updated`). A hold on an end
   * customer also publishes `end_customer_updated` for each other
   * end-customer row with the same trimmed, non-empty business id.
   *
   * `error_code` values returned with `422` (nothing is recorded, for
   * any of the named targets):
   *
   * - `financing_hold_invalid_target` — the request names no target, or
   *   a named target does not exist.
   * - `financing_hold_reason_required` — `reason` is empty after
   *   trimming.
   *
   * Requires the `financing-holds:place` permission (403 otherwise).
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `placeFinancingHold$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  placeFinancingHold(params: {
    context?: HttpContext
    body: PlaceFinancingHoldRequest
  }
): Observable<Array<FinancingHold>> {

    return this.placeFinancingHold$Response(params).pipe(
      map((r: StrictHttpResponse<Array<FinancingHold>>) => r.body as Array<FinancingHold>)
    );
  }

  /**
   * Path part for operation releaseFinancingHolds
   */
  static readonly ReleaseFinancingHoldsPath = '/financing-holds/release';

  /**
   * Release several active financing holds with one note, all or none.
   *
   * Releases every named hold, all or none, recording on each the
   * trimmed note, the caller as the releasing user, and the release
   * time, which the holds of one request share. The named holds may
   * have different targets and scopes, and need not have been placed
   * together. The response lists the released holds in ascending id
   * order.
   *
   * A released hold no longer applies to any invoice, and stays stored
   * and listed as history. Holds the request does not name, including
   * other active holds on the same target, are unaffected. Each
   * released hold publishes its target's update event on the
   * notification stream (`client_updated`, `end_customer_updated` or
   * `invoice_updated`). A released hold on an end customer also
   * publishes `end_customer_updated` for each other end-customer row
   * with the same trimmed, non-empty business id.
   *
   * Releasing holds does not advance any invoice, pay anything out or
   * resolve an operational task: the handler does that afterwards.
   *
   * `error_code` values returned with `422` (nothing changes, for any
   * of the named holds):
   *
   * - `financing_hold_release_note_required` — `note` is empty after
   *   trimming.
   * - `financing_hold_invalid_selection` — `hold_ids` is empty, holds
   *   more than 50 ids, names an id twice, or holds an id below 1.
   * - `financing_hold_not_found` — a named hold does not exist.
   *   `details.hold_ids` lists the unknown ids. When the request names
   *   both unknown and released holds, this code is the one returned.
   * - `financing_hold_already_released` — a named hold is already
   *   released. `details.hold_ids` lists the released ids. Their
   *   recorded releases are unchanged.
   *
   * Requires the `financing-holds:release` permission (403 otherwise),
   * also for the user who placed the holds.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `releaseFinancingHolds()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  releaseFinancingHolds$Response(params: {
    context?: HttpContext
    body: ReleaseFinancingHoldsRequest
  }
): Observable<StrictHttpResponse<Array<FinancingHold>>> {

    const rb = new RequestBuilder(this.rootUrl, FinancingHoldsService.ReleaseFinancingHoldsPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<FinancingHold>>;
      })
    );
  }

  /**
   * Release several active financing holds with one note, all or none.
   *
   * Releases every named hold, all or none, recording on each the
   * trimmed note, the caller as the releasing user, and the release
   * time, which the holds of one request share. The named holds may
   * have different targets and scopes, and need not have been placed
   * together. The response lists the released holds in ascending id
   * order.
   *
   * A released hold no longer applies to any invoice, and stays stored
   * and listed as history. Holds the request does not name, including
   * other active holds on the same target, are unaffected. Each
   * released hold publishes its target's update event on the
   * notification stream (`client_updated`, `end_customer_updated` or
   * `invoice_updated`). A released hold on an end customer also
   * publishes `end_customer_updated` for each other end-customer row
   * with the same trimmed, non-empty business id.
   *
   * Releasing holds does not advance any invoice, pay anything out or
   * resolve an operational task: the handler does that afterwards.
   *
   * `error_code` values returned with `422` (nothing changes, for any
   * of the named holds):
   *
   * - `financing_hold_release_note_required` — `note` is empty after
   *   trimming.
   * - `financing_hold_invalid_selection` — `hold_ids` is empty, holds
   *   more than 50 ids, names an id twice, or holds an id below 1.
   * - `financing_hold_not_found` — a named hold does not exist.
   *   `details.hold_ids` lists the unknown ids. When the request names
   *   both unknown and released holds, this code is the one returned.
   * - `financing_hold_already_released` — a named hold is already
   *   released. `details.hold_ids` lists the released ids. Their
   *   recorded releases are unchanged.
   *
   * Requires the `financing-holds:release` permission (403 otherwise),
   * also for the user who placed the holds.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `releaseFinancingHolds$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  releaseFinancingHolds(params: {
    context?: HttpContext
    body: ReleaseFinancingHoldsRequest
  }
): Observable<Array<FinancingHold>> {

    return this.releaseFinancingHolds$Response(params).pipe(
      map((r: StrictHttpResponse<Array<FinancingHold>>) => r.body as Array<FinancingHold>)
    );
  }

  /**
   * Path part for operation releaseFinancingHold
   */
  static readonly ReleaseFinancingHoldPath = '/financing-holds/{id}/release';

  /**
   * Release an active financing hold.
   *
   * Releases an active hold, recording the trimmed note, the caller as
   * the releasing user, and the release time. The released hold no
   * longer applies to any invoice, and stays stored and listed as
   * history. Other holds, including other active holds on the same
   * target, are unaffected. Releasing a hold publishes the target's
   * update event on the notification stream (`client_updated`,
   * `end_customer_updated` or `invoice_updated`). Releasing a hold on an
   * end customer also publishes `end_customer_updated` for each other
   * end-customer row with the same trimmed, non-empty business id.
   *
   * Releasing a hold does not advance any invoice, pay anything out or
   * resolve an operational task: the handler does that afterwards.
   *
   * `error_code` values returned with `422` (nothing changes):
   *
   * - `financing_hold_release_note_required` — `note` is empty after
   *   trimming.
   * - `financing_hold_already_released` — the hold is already released.
   *   Its recorded release is unchanged.
   *
   * Requires the `financing-holds:release` permission (403 otherwise),
   * also for the user who placed the hold.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `releaseFinancingHold()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  releaseFinancingHold$Response(params: {
    id: number;
    context?: HttpContext
    body: ReleaseFinancingHoldRequest
  }
): Observable<StrictHttpResponse<FinancingHold>> {

    const rb = new RequestBuilder(this.rootUrl, FinancingHoldsService.ReleaseFinancingHoldPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<FinancingHold>;
      })
    );
  }

  /**
   * Release an active financing hold.
   *
   * Releases an active hold, recording the trimmed note, the caller as
   * the releasing user, and the release time. The released hold no
   * longer applies to any invoice, and stays stored and listed as
   * history. Other holds, including other active holds on the same
   * target, are unaffected. Releasing a hold publishes the target's
   * update event on the notification stream (`client_updated`,
   * `end_customer_updated` or `invoice_updated`). Releasing a hold on an
   * end customer also publishes `end_customer_updated` for each other
   * end-customer row with the same trimmed, non-empty business id.
   *
   * Releasing a hold does not advance any invoice, pay anything out or
   * resolve an operational task: the handler does that afterwards.
   *
   * `error_code` values returned with `422` (nothing changes):
   *
   * - `financing_hold_release_note_required` — `note` is empty after
   *   trimming.
   * - `financing_hold_already_released` — the hold is already released.
   *   Its recorded release is unchanged.
   *
   * Requires the `financing-holds:release` permission (403 otherwise),
   * also for the user who placed the hold.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `releaseFinancingHold$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  releaseFinancingHold(params: {
    id: number;
    context?: HttpContext
    body: ReleaseFinancingHoldRequest
  }
): Observable<FinancingHold> {

    return this.releaseFinancingHold$Response(params).pipe(
      map((r: StrictHttpResponse<FinancingHold>) => r.body as FinancingHold)
    );
  }

}
